// Logique pure de la tour de contrôle : filtres, segments, libellés (repris
// de Faast). Testée sans rendu (tower-model.test.ts).
import type { TowerEventRow, TowerSeverity, TowerSource, TowerState, TowerTicketRow, TowerTriageCard } from '@feedbaacks/sdk'

/** Teintes, rendues par les classes tw-tone-* (jetons du thème). */
export type Tone = 'sky' | 'accent' | 'violet' | 'cyan' | 'amber' | 'emerald' | 'red' | 'muted'

export const STATE_META: Record<TowerState, { label: string; tone: Tone }> = {
  queued: { label: 'En file', tone: 'sky' },
  running: { label: 'Agent au travail', tone: 'accent' },
  verifying: { label: 'CI en cours', tone: 'accent' },
  reviewing: { label: 'En revue', tone: 'violet' },
  building: { label: 'Build', tone: 'cyan' },
  preview: { label: 'Preview', tone: 'cyan' },
  awaiting_approval: { label: 'Attente prod', tone: 'amber' },
  approved: { label: 'PR ouverte', tone: 'amber' },
  deployed: { label: 'Livré', tone: 'emerald' },
  failed: { label: 'Échoué', tone: 'red' },
  stopped: { label: 'Arrêté', tone: 'muted' },
}

export const SEVERITY_META: Record<TowerSeverity, { label: string; tone: Tone }> = {
  low: { label: 'Faible', tone: 'muted' },
  medium: { label: 'Moyenne', tone: 'sky' },
  high: { label: 'Haute', tone: 'amber' },
  critical: { label: 'Critique', tone: 'red' },
}

export const TYPE_LABEL = { fix: 'Correctif', feature: 'Évolution', chore: 'Entretien' } as const

export const FEEDBACK_STATUS: Record<string, string> = {
  open: 'Ouvert', under_review: 'En examen', planned: 'Planifié', in_progress: 'En cours', on_hold: 'Au frigo',
  shipped: 'Livré', declined: 'Refusé', duplicate: 'Doublon',
}

/** Filtres de la liste « Demandes ». `archives` lit la liste à part des
 *  tickets archivés ; les autres filtrent les tickets vivants. */
export type TicketFilter = 'tous' | 'actifs' | 'attente' | 'echoues' | 'livres' | 'archives'
type LiveFilter = Exclude<TicketFilter, 'archives'>

const ACTIVE: TowerState[] = ['queued', 'running', 'verifying', 'reviewing', 'building', 'preview', 'approved']

export function isActive(state: TowerState): boolean {
  return ACTIVE.includes(state)
}

export const FILTERS: { key: LiveFilter; label: string; match: (t: TowerTicketRow) => boolean }[] = [
  { key: 'tous', label: 'Tous', match: () => true },
  { key: 'actifs', label: 'En cours', match: (t) => isActive(t.state) },
  { key: 'attente', label: 'Attente prod', match: (t) => t.state === 'awaiting_approval' },
  { key: 'echoues', label: 'Échoués', match: (t) => t.state === 'failed' || t.state === 'stopped' },
  { key: 'livres', label: 'Livrés', match: (t) => t.state === 'deployed' },
]

export function parseFilter(raw: string | null): TicketFilter {
  return raw === 'archives' || FILTERS.some((f) => f.key === raw) ? (raw as TicketFilter) : 'tous'
}

export function filterTickets(tickets: TowerTicketRow[], f: TicketFilter): TowerTicketRow[] {
  const def = FILTERS.find((x) => x.key === f) ?? FILTERS[0]
  return tickets.filter(def.match)
}

export function filterCounts(tickets: TowerTicketRow[]): Record<LiveFilter, number> {
  return Object.fromEntries(FILTERS.map((f) => [f.key, tickets.filter(f.match).length])) as Record<LiveFilter, number>
}

/** Groupes de la vue « Tous » : ce qui attend un humain d'abord. */
const GROUPS: { key: string; label: string; match: (t: TowerTicketRow) => boolean }[] = [
  { key: 'action', label: 'Action requise', match: (t) => t.state === 'awaiting_approval' || t.state === 'failed' },
  { key: 'cours', label: 'En cours', match: (t) => isActive(t.state) },
  { key: 'termines', label: 'Terminés', match: (t) => t.state === 'deployed' || t.state === 'stopped' },
]

export interface TicketGroup { key: string; label: string; rows: TowerTicketRow[] }

/** Les tickets d'un filtre, en groupes (un seul groupe hors de « Tous »).
 *  L'ordre des groupes est aussi celui de la navigation au clavier. */
export function groupTickets(rows: TowerTicketRow[], f: TicketFilter): TicketGroup[] {
  if (f !== 'tous') return [{ key: f, label: '', rows }]
  return GROUPS.map((g) => ({ key: g.key, label: g.label, rows: rows.filter(g.match) })).filter((g) => g.rows.length > 0)
}

const fold = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

/** Recherche plein texte (code, titre, résumé, projet, société), sans
 *  accents ni casse, et filtre de projet. */
export function searchTickets(rows: TowerTicketRow[], query: string, project: string): TowerTicketRow[] {
  const words = fold(query).split(/\s+/).filter(Boolean)
  return rows.filter((t) => {
    if (project && t.project !== project) return false
    if (words.length === 0) return true
    const hay = fold([t.code, t.title, t.summary, t.project, t.company ?? ''].join(' '))
    return words.every((w) => hay.includes(w))
  })
}

/** Les tickets qu'un geste « Archiver tout » rangerait. */
export function archivable(rows: TowerTicketRow[]): TowerTicketRow[] {
  return rows.filter((t) => t.can_archive && !t.archived_at)
}

/** Le voisin d'un ticket dans la liste affichée (navigation, archivage). */
export function neighbour(rows: TowerTicketRow[], code: string | null, step: 1 | -1): string | null {
  if (rows.length === 0) return null
  const i = rows.findIndex((t) => t.code === code)
  if (i === -1) return rows[step === 1 ? 0 : rows.length - 1].code
  return rows[Math.min(rows.length - 1, Math.max(0, i + step))].code
}

/** Après l'archivage du ticket affiché : le suivant, sinon le précédent. */
export function nextAfterRemoval(rows: TowerTicketRow[], code: string): string | null {
  const i = rows.findIndex((t) => t.code === code)
  if (i === -1) return null
  return rows[i + 1]?.code ?? rows[i - 1]?.code ?? null
}

export const STEP_NAMES = ['Tri', 'Agent', 'Revue', 'Preview', 'Prod', 'Livré'] as const

export type SegmentStatus = 'done' | 'current' | 'failed' | 'todo'

/** Les 6 segments d'avancement. `stage` vient du serveur (1 = Agent … 6 =
 *  Livré ; le Tri est toujours fait). Un échec colore son segment en rouge. */
export function segments(state: TowerState, stage: number): SegmentStatus[] {
  if (state === 'deployed') return STEP_NAMES.map(() => 'done')
  const failed = state === 'failed' || state === 'stopped'
  return STEP_NAMES.map((_, i) => {
    if (i === 0 || i < stage) return 'done'
    if (i === stage) return failed ? 'failed' : 'current'
    return 'todo'
  })
}

export function sourceLabel(source: TowerSource, company?: string): string {
  if (source === 'team') return 'Équipe'
  return company ? `Client · ${company}` : 'Client'
}

export function money(usd: number): string {
  return `${usd.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`
}

export function durationLabel(seconds: number): string {
  if (!seconds || seconds < 60) return '< 1 min'
  const m = Math.round(seconds / 60)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r ? `${h} h ${r}` : `${h} h`
}

/** Pastille d'un retour à trier. */
export function triageChip(card: TowerTriageCard): { label: string; tone: Tone } {
  if (card.verdict === 'injection') return { label: 'Injection suspectée', tone: 'red' }
  if (card.status === 'error') return { label: 'Tri en échec', tone: 'red' }
  if (card.status === 'pending') return { label: 'En cours de tri', tone: 'muted' }
  if (card.status === 'asked') return { label: 'Question posée', tone: 'sky' }
  if (card.verdict === 'unclear') return { label: 'À préciser', tone: 'amber' }
  return { label: 'Prêt', tone: 'emerald' }
}

/** Les actions permises sur une carte : jamais de création sur injection. */
export function triageActions(card: TowerTriageCard): { create: boolean; edit: boolean; ask: boolean; reject: boolean; report: boolean } {
  const injection = card.verdict === 'injection'
  const decidable = card.status === 'proposed' || card.status === 'asked'
  return {
    create: !injection && card.can_create && decidable,
    edit: !injection && decidable,
    ask: !injection && card.verdict === 'unclear' && card.status === 'proposed',
    reject: !injection && card.status !== 'pending',
    report: injection,
  }
}

export function confidenceTone(c: number): Tone {
  if (c >= 0.8) return 'emerald'
  if (c >= 0.6) return 'amber'
  return 'red'
}

export function redactionNote(redacted: Record<string, number>): string {
  const n = Object.values(redacted).reduce((a, b) => a + b, 0)
  if (n === 0) return 'Aucune donnée personnelle détectée.'
  const parts = Object.entries(redacted).map(([k, v]) => `${v} ${k}`)
  return `${n} donnée${n > 1 ? 's' : ''} masquée${n > 1 ? 's' : ''} avant le tri : ${parts.join(', ')}.`
}

/** La carte d'approbation ne s'affiche qu'à l'état awaiting_approval. */
export function showApprovalCard(state: TowerState): boolean {
  return state === 'awaiting_approval'
}

/** Heure courte « 14:05 », précédée du jour « 13/10 » hors d'aujourd'hui. */
export function shortTime(iso: string, now = Date.now()): string {
  const d = new Date(iso)
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  if (d.toDateString() === new Date(now).toDateString()) return time
  return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${time}`
}

export function remainingLabel(expiresAt: string | undefined, now: number): string {
  if (!expiresAt) return ''
  const h = Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 3_600_000))
  return h > 0 ? `Carte valable ${h} h` : 'Carte bientôt expirée'
}

const rtf = new Intl.RelativeTimeFormat('fr', { numeric: 'auto' })

/** « il y a 5 minutes », « hier ». */
export function relative(iso: string, now = Date.now()): string {
  const s = (new Date(iso).getTime() - now) / 1000
  if (Number.isNaN(s)) return ''
  for (const [unit, size] of [['day', 86400], ['hour', 3600], ['minute', 60]] as const) {
    if (Math.abs(s) >= size) return rtf.format(Math.round(s / size), unit)
  }
  return 'à l’instant'
}

/** Un détail d'événement : court, il suit le message ; long, il se déplie. */
export interface EventDetail { label: string; text: string; long: boolean }

/** Les attributs qui expliquent un refus, du plus précis au plus sommaire. */
const DETAIL_LABELS: [key: string, label: string][] = [
  ['notes', 'Dernier refus'],
  ['comments', 'Remarques de la revue'],
  ['log_tail', 'Fin du journal de CI'],
  ['note', 'Note de l\'approbateur'],
  ['team_note', 'Remarque'],
  ['summary', 'Résumé'],
]

export function eventDetails(e: TowerEventRow): EventDetail[] {
  const out: EventDetail[] = []
  for (const [key, label] of DETAIL_LABELS) {
    const v = e.attrs?.[key]
    if (typeof v !== 'string' || !v.trim()) continue
    const text = v.trim()
    if (key === 'summary' && e.message.includes(text)) continue
    out.push({ label, text, long: text.includes('\n') || text.length > 160 })
  }
  return out
}

/** Pourquoi le ticket s'est arrêté : le détail le plus précis du dernier refus. */
export function failureReason(events: TowerEventRow[]): EventDetail | undefined {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]
    // Les remarques de l'équipe disent ce qu'on attend, pas pourquoi ça a échoué.
    if (e.status === 'ok' || e.step === 'équipe') continue
    const d = eventDetails(e)
    if (d.length) return d[0]
  }
  return undefined
}
